<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Conceptos remunerativos</h1>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por nombre o código"
            @input="search = $event"
            @search="search = $event"
          />
          <wz-button @click="$router.push({ name: 'payroll-concept-create' })">Agregar</wz-button>
        </template>

        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'code' }" @click="toggleSort('code')">
              Código <i :class="['wz-th-sort-icon', sortIcon('code')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'name' }" @click="toggleSort('name')">
              Nombre <i :class="['wz-th-sort-icon', sortIcon('name')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'type' }" @click="toggleSort('type')">
              Tipo <i :class="['wz-th-sort-icon', sortIcon('type')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'nature' }" @click="toggleSort('nature')">
              Naturaleza <i :class="['wz-th-sort-icon', sortIcon('nature')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'affectsFifthCategory' }" @click="toggleSort('affectsFifthCategory')">
              Afecta IR 5ta <i :class="['wz-th-sort-icon', sortIcon('affectsFifthCategory')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'status' }" @click="toggleSort('status')">
              Estado <i :class="['wz-th-sort-icon', sortIcon('status')]"></i>
            </th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredList.length === 0">
            <td colspan="7" class="text-center text-muted py-4">No se encontraron resultados.</td>
          </tr>
          <tr v-for="o in sortedList(filteredList)" :key="o.id">
            <td>{{ o.code }}</td>
            <td>{{ o.name }}</td>
            <td>
              <wz-badge :variant="typeVariant(o.type)" pill>
                {{ getTypeLabel(o.type) }}
              </wz-badge>
            </td>
            <td>{{ getNatureLabel(o.nature) }}</td>
            <td>{{ o.affectsFifthCategory ? 'Sí' : 'No' }}</td>
            <td>
              <wz-badge :variant="statusVariant(o.status)" pill>
                {{ getStatus(o.status).label }}
              </wz-badge>
            </td>
            <td style="text-align: center;">
              <wz-dropdown
                :items="[
                  { key: 'edit',   label: 'Editar' },
                  { key: 'status', label: getStatus(o.status).action },
                ]"
                placement="left"
                @click="handleAction(o, $event)"
              />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <span>{{ filteredList.length }} registro(s)</span>
      </wz-table-footer>

    </wz-table-container>

    <wz-confirm-modal
      v-model="confirmModal.show"
      :title="confirmModal.title"
      :description="confirmModal.description"
      :accept-text="confirmModal.acceptText"
      :cancel-text="confirmModal.cancelText"
      @confirm="onModalConfirm"
      @cancel="closeConfirmModal"
    />
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { Util } from "@wizepe/wizeui"
import { FilterMixin } from "@wizepe/wizeui"
import { SortMixin, ConfirmModalMixin } from '@wizepe/wizeui'
import { Constants as LocalConstants } from '@/core/utils'
import { PayrollConceptService } from '@/core/services'

export default {
  mixins: [SortMixin, FilterMixin, ConfirmModalMixin],
  data() {
    return {
      list: [],
      search: '',
      filterOptions: [
        {
          label: 'Estado',
          key: 'status',
          type: 'SINGLE',
          options: [LocalConstants.PayrollConceptStatus.ACTIVE, LocalConstants.PayrollConceptStatus.INACTIVE],
        },
        {
          label: 'Tipo',
          key: 'type',
          type: 'SINGLE',
          options: [LocalConstants.PayrollConceptType.ADICIONAL, LocalConstants.PayrollConceptType.DESCUENTO],
        },
      ],
    }
  },
  computed: {
    filteredList() {
      let result = this.list
      if (this.filters.status != null) {
        result = result.filter(o => parseInt(o.status) === this.filters.status)
      }
      if (this.filters.type != null) {
        result = result.filter(o => o.type === this.filters.type)
      }
      if (this.search) {
        const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
        const q = norm(this.search)
        result = result.filter(o => norm(o.name).includes(q) || norm(o.code).includes(q))
      }
      return result
    },
  },
  methods: {
    statusVariant(status) {
      return this.getStatus(status)?.color || 'dark'
    },
    typeVariant(type) {
      return type === 'DESCUENTO' ? 'danger-solid' : 'success-solid'
    },
    handleAction(o, item) {
      if (item.key === 'edit')   this.$router.push({ name: 'payroll-concept-edit', params: { id: o.id } })
      if (item.key === 'status') this.openChangeStatus(o)
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await PayrollConceptService.list()
        this.list = (resp.payload || []).map(item => ({ ...item }))
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los conceptos')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    getStatus(key) {
      return Util.searchValue(parseInt(key), LocalConstants.PayrollConceptStatus)
    },
    getTypeLabel(type) {
      const t = LocalConstants.PayrollConceptType[type]
      return t ? t.label : type
    },
    getNatureLabel(nature) {
      const n = LocalConstants.PayrollConceptNature[nature]
      return n ? n.label : nature
    },
    openChangeStatus(o) {
      this.selectedOption = o
      this.openConfirmModal({
        title:       'Cambiar estado',
        description: '¿Está seguro que desea cambiar el estado de este concepto?',
        acceptText:  'Aceptar',
        action:      'update',
      })
    },
    onModalConfirm() {
      this.closeConfirmModal()
      if (this.confirmModal.action === 'update') this.changeStatus()
    },
    async changeStatus() {
      try {
        await this.$store.dispatch('app/loading', true)
        const current   = parseInt(this.selectedOption.status)
        const newStatus = current === LocalConstants.PayrollConceptStatus.ACTIVE.key
          ? LocalConstants.PayrollConceptStatus.INACTIVE.key
          : LocalConstants.PayrollConceptStatus.ACTIVE.key
        await PayrollConceptService.changeStatus(this.selectedOption.id, newStatus)
        await this.loadData()
        Alert.success('Estado actualizado correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al cambiar el estado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    await this.loadData()
  },
}
</script>

<style lang="stylus">
</style>
